import { createContext, useContext, useState, useEffect } from "react";

const CountryContext = createContext();

// All country-specific configuration in one place
export const COUNTRY_CONFIG = {
  NG: {
    code: "NG",
    name: "Nigeria",
    flag: "🇳🇬",
    currency: "NGN",
    currencySymbol: "₦",
    currencyCode: "NGN",
    phoneCode: "+234",
    phoneDigits: 10,
    accountDigits: 10,
    apps: {
      opay: true,
      palmpay: true,
      kuda: true,
      moniepoint: true,
      luno: true,
    },
    welcomeText: "Welcome from Nigeria",
  },

  GH: {
    code: "GH",
    name: "Ghana",
    flag: "🇬🇭",
    currency: "GHS",
    currencySymbol: "₵",
    currencyCode: "GHS",
    phoneCode: "+233",
    phoneDigits: 9,
    accountDigits: 13,
    apps: {
      mtn: true,
      telecel: true,
      airteltigo: true,
      gcb: true,
      ecobank: true,
      hubtel: true,
    },
    welcomeText: "Akwaaba from Ghana",
  },

  // ===== NEW: NAMIBIA =====
  NA: {
    code: "NA",
    name: "Namibia",
    flag: "🇳🇦",
    currency: "NAD",
    currencySymbol: "N$",
    currencyCode: "NAD",
    phoneCode: "+264",
    phoneDigits: 9,
    accountDigits: 16, // Namibian bank accounts vary; 16 covers most
    apps: {
      fnbNamibia: true,
      standardBankNamibia: true,
      bankWindhoek: true,
      nedbankNamibia: true,
      smallBusinessBank: true,
    },
    welcomeText: "Welcome from Namibia",
    // Payment: USD card via Nomba link (Nomba accepts USD cards)
    paymentMethod: "card_usd",
    // Registration fee in USD (converted to NAD at live rate for display)
    registrationFeeUSD: 6, // approx equivalent to NGN 10,200
    // Subscription plans in USD (same as other countries)
    subscriptionPlansUSD: {
      Basic: 25.0,
      Premium: 35.0,
      Elite: 55.0,
    },
    // Namibian banks list for Elite bank transfer feature
    banks: [
      "Bank Windhoek",
      "First National Bank Namibia",
      "Standard Bank Namibia",
      "Nedbank Namibia",
      "Small Business Bank (SBB)",
      "Bank of Namibia",
      "Letshego Bank Namibia",
      "Trustco Bank Namibia",
      "Development Bank of Namibia",
      "Agricultural Bank of Namibia",
    ],
  },
};

export function CountryProvider({ children }) {
  const [country, setCountryState] = useState(() => {
    const saved = localStorage.getItem("userCountry");
    return saved && COUNTRY_CONFIG[saved] ? saved : null;
  });

  const setCountry = (code) => {
    if (COUNTRY_CONFIG[code]) {
      localStorage.setItem("userCountry", code);
      setCountryState(code);
    }
  };

  const resetCountry = () => {
    localStorage.removeItem("userCountry");
    setCountryState(null);
  };

  // Get current country config (defaults to Nigeria if somehow null)
  const config = country ? COUNTRY_CONFIG[country] : COUNTRY_CONFIG.NG;

  return (
    <CountryContext.Provider value={{
      country,              // "NG" | "GH" | "NA" | null
      config,               // full config object
      setCountry,           // function to set country
      resetCountry,         // function to clear country
      isNigeria: country === "NG",
      isGhana: country === "GH",
      isNamibia: country === "NA",  // ✅ NEW
      hasSelected: !!country,
    }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error("useCountry must be used within CountryProvider");
  }
  return context;
}
